import { IconToggleButton, LuIcon } from "@overmap-ai/blocks"
import { type ComponentPropsWithRef, memo, useCallback } from "react"

import { TextAlign } from "../../enums"
import { useEditorFocused, useFallbackEditor, useTextAlignState } from "../../hooks"
import { textAlignDataMapping } from "../mappings"
import type { OptionalEditorProps } from "../typings"

interface TextAlignToggleButtonProps
	extends Omit<ComponentPropsWithRef<typeof IconToggleButton>, "children">, OptionalEditorProps {
	alignment: TextAlign
}

export const TextAlignToggleButton = memo<TextAlignToggleButtonProps>((props) => {
	const { ref, alignment, editor: editorFromProps, pressed, onPressedChange, disabled, ...rest } = props

	const editor = useFallbackEditor(editorFromProps)
	const isEditorFocused = useEditorFocused(editor)
	const state = useTextAlignState(editor)

	const handlePressedChange = useCallback(
		(pressed: boolean) => {
			onPressedChange?.(pressed)
			editor?.chain().focus().toggleTextAlign(alignment).run()
		},
		[onPressedChange, editor, alignment],
	)

	return (
		<IconToggleButton
			ref={ref}
			pressed={pressed ?? (state.isActive(alignment) && isEditorFocused)}
			onPressedChange={handlePressedChange}
			disabled={disabled ?? !state.canToggle(alignment)}
			{...rest}
		>
			<LuIcon icon={textAlignDataMapping[alignment].icon} />
		</IconToggleButton>
	)
})
TextAlignToggleButton.displayName = "TextAlignToggleButton"
